import { Button, ButtonProps, styled } from '@mui/material'

/**
 * FullWidthButton component
 * This component styles a button to be full width and visually appealing.
 * It uses MUI's Button component and allows for custom styling.
 * @param {ButtonProps} props - The properties for the button component.
 * @returns {JSX.Element} The rendered button component.
 * @example
 * <FullWidthButton onClick={handleClick}>Click Me</FullWidthButton>
 */
const FullWidthButton = styled(Button)(({ theme }) => ({
  borderRadius: '8px',
  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
  textTransform: 'none',
  marginTop: 24,
  padding: theme.spacing(1.5, 2),
  fontWeight: 'bold',
  backgroundColor: theme.palette.background.default,
  color: theme.palette.text.primary,
  width: '100%',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
    boxShadow: '0 2px 10px rgba(0, 0, 0, 0.15)'
  }
}))

/** CustomListItemButtonProps type extends ButtonProps and includes a label */
interface CustomListItemButtonProps extends ButtonProps {
  /** The label to display on the button */
  label: string
}

/**
 * CustomListItemButton component
 * This component renders a styled button that can be used as a list item button.
 * It uses MUI's Button component and allows for custom styling.
 * @param {CustomListItemButtonProps} props - The properties for the custom list item button component.
 * @returns {JSX.Element} The rendered custom list item button component.
 */
export const CustomListItemButton: React.FC<CustomListItemButtonProps> = ({
  label,
  ...props
}) => {
  return <FullWidthButton {...props}>{label}</FullWidthButton>
}
